/**
 * Landing Page
 * Apple-inspired public homepage for AnchorPals
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import {
  Anchor,
  Ship,
  Users,
  MapPin,
  Star,
  Clock,
  MessageCircle,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Yacht Mooring Helpers Across the Mediterranean"
        description="Find local mooring helpers across the Mediterranean. Stern-to mooring, med mooring, real-time docking assistance at 500+ Med ports. Free to join — launching soon."
        path="/"
      />
      {/* Navigation */}
      <nav className="glass fixed top-0 left-0 right-0 z-50"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-12">
            <div className="flex items-center">
              <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-10 w-10 rounded-lg" />
              <span className="ml-2.5 text-lg font-bold text-navy-900 tracking-tight">
                AnchorPals
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="/blog"
                className="text-sm text-navy-600 hover:text-navy-900 transition-colors duration-300"
              >
                Blog
              </a>
              <Link
                to="/login"
                className="text-sm text-navy-600 hover:text-navy-900 transition-colors duration-300"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm bg-primary-600 text-white hover:bg-primary-700 py-1.5 px-4 rounded-full transition-all duration-300"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-20 lg:pt-28 lg:pb-32 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-8">
            <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-32 w-32 sm:h-36 sm:w-36 rounded-3xl" />
            <span className="ml-6 text-5xl sm:text-6xl font-bold text-navy-900 tracking-tight">
              AnchorPals
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-navy-900 leading-[1.05] tracking-tight">
            Your Crew at
            <br />
            <span className="text-primary-600">Every Port.</span>
          </h1>
          <p className="mt-6 text-xl sm:text-2xl text-navy-500 max-w-2xl mx-auto font-light leading-relaxed">
            Connect with local helpers for mooring assistance.
            <br />
            Whether arriving at a new marina or needing an extra hand,
            <br />
            AnchorPals has you covered.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?type=owner"
              className="btn-primary text-base px-8 py-3.5"
            >
              Register as Owner/Captain
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/register?type=helper"
              className="btn-secondary text-base px-8 py-3.5"
            >
              Become a Helper
            </Link>
          </div>
        </div>
      </section>


      {/* How It Works - Dark Background */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-navy-400 font-light">
              Getting help at the dock has never been easier
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Video */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full h-auto"
              >
                <source src="/videos/how-it-works.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Steps */}
            <div className="space-y-8">
              {[
                {
                  step: '1',
                  title: 'Post Your Request',
                  description:
                    'Tell us when and where you need help. Add details about your yacht and the type of assistance required.',
                  icon: MapPin,
                },
                {
                  step: '2',
                  title: 'Choose Your Helper',
                  description:
                    'Browse local helpers, check their ratings and experience, then select the best match.',
                  icon: Users,
                },
                {
                  step: '3',
                  title: 'Dock with Confidence',
                  description:
                    'Chat with your helper, coordinate arrival, and enjoy a smooth mooring experience.',
                  icon: Ship,
                },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-5">
                  <div className="flex-shrink-0 h-12 w-12 bg-primary-600 rounded-full flex items-center justify-center text-white text-lg font-bold">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-navy-400 font-light leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Greenies Section - Moved here */}
      <section className="py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
            <div>
              <span className="inline-block bg-primary-600 text-white font-semibold text-sm uppercase tracking-wider px-4 py-2 rounded-full mb-4">New to Yachting?</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
                Your Launchpad into the Industry
              </h2>
              <p className="mt-6 text-lg text-navy-500 font-light leading-relaxed">
                AnchorPals isn't just for experienced crew — it's the perfect starting point for
                <strong className="text-navy-700"> greenies</strong> (newcomers) looking to break into superyachts.
              </p>
              <p className="mt-4 text-lg text-navy-500 font-light leading-relaxed">
                As a helper, every yacht you assist is a networking opportunity. Captains notice
                reliable line handlers. Chief stews remember the helper who went above and beyond.
                Many full-time positions start with a chance encounter at the dock.
              </p>

              <ul className="mt-8 space-y-4">
                {[
                  'Make real industry contacts with captains and crew',
                  'Build your reputation before you have sea time',
                  'Learn mooring, lines, and marina operations hands-on',
                  'Get noticed — reliability at the dock opens doors',
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-navy-600">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/register?type=helper"
                className="inline-flex items-center mt-10 btn-primary"
              >
                Start Building Your Reputation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="mt-12 lg:mt-0">
              <div className="bg-navy-100 rounded-apple-xl p-6">
                <span className="inline-block bg-primary-600 text-white font-semibold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full">Featured Guide</span>
                <a href="/blog/how-to-get-started-superyacht-career" className="block mt-3 group">
                  <h3 className="text-xl font-bold text-navy-900 group-hover:text-primary-600 transition-colors">
                    So You Want to Work on a Superyacht? Here's How to Get Started
                  </h3>
                  <p className="mt-3 text-navy-500 font-light leading-relaxed">
                    The superyacht industry is one of the most exciting career paths for adventurous
                    souls seeking a life less ordinary. Here's everything you need to know to break in.
                  </p>
                  <span className="inline-flex items-center mt-4 text-primary-600 font-medium text-sm">
                    Read the full guide
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </a>
              </div>

              <div className="mt-6 bg-navy-100 rounded-apple-xl p-6">
                <span className="inline-block bg-navy-700 text-white font-semibold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full mb-4">More Career Guides</span>
                <ul className="space-y-3">
                  <li>
                    <a href="/blog/stcw-certificate-guide" className="text-navy-600 hover:text-primary-600 transition-colors">
                      The Essential STCW Certificate →
                    </a>
                  </li>
                  <li>
                    <a href="/blog/day-work-landing-first-yacht-job" className="text-navy-600 hover:text-primary-600 transition-colors">
                      Day Work: Landing Your First Job →
                    </a>
                  </li>
                  <li>
                    <a href="/blog/where-to-stay-antibes-yacht-crew" className="text-navy-600 hover:text-primary-600 transition-colors">
                      Where to Stay in Antibes →
                    </a>
                  </li>
                  <li>
                    <a href="/blog/yacht-crew-cv-what-captains-want" className="text-navy-600 hover:text-primary-600 transition-colors">
                      Building Your Yacht Crew CV →
                    </a>
                  </li>
                </ul>
                <a href="/blog" className="inline-flex items-center mt-4 text-primary-600 font-medium text-sm">
                  View all articles
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Marina */}
      <section className="py-24 bg-navy-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <span className="text-primary-600 font-medium text-sm uppercase tracking-wider">Featured Marina</span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
                Port Vauban, Antibes
              </h2>
              <p className="mt-6 text-lg text-navy-500 font-light leading-relaxed">
                Europe's largest marina and the beating heart of the superyacht industry.
                With over 1,600 berths and capacity for yachts exceeding 100 metres,
                Port Vauban is where careers are launched and connections are made.
              </p>
              <p className="mt-4 text-lg text-navy-500 font-light leading-relaxed">
                Home to the famous Quai des Milliardaires, this is where aspiring crew
                walk the docks each morning looking for opportunities — and where
                AnchorPals helpers make their mark.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/blog/port-vauban-antibes-guide"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
                >
                  Read the full guide
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <a
                  href="/blog/category/marinas"
                  className="inline-flex items-center text-navy-600 hover:text-primary-600 font-medium transition-colors duration-300"
                >
                  View all marinas
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="bg-white rounded-apple-xl p-8">
                <Anchor className="h-12 w-12 text-primary-600 mb-4" />
                <div className="text-2xl font-bold text-navy-900 mb-2">The Yachting Capital</div>
                <ul className="space-y-3 text-navy-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>1,600+ berths for yachts up to 163m</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>20 minutes from Nice Airport</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Crew agencies and training centres nearby</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Peak season: April to October</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
              Why AnchorPals?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Clock,
                title: 'Real-Time & Scheduled',
                description:
                  'Plan ahead or request help instantly. We adapt to your schedule.',
              },
              {
                icon: Star,
                title: 'Trusted Reviews',
                description:
                  'Make informed decisions with our two-way rating and review system.',
              },
              {
                icon: MessageCircle,
                title: 'In-App Chat',
                description:
                  'Coordinate seamlessly with secure, direct messaging.',
              },
              {
                icon: MapPin,
                title: 'Global Coverage',
                description:
                  'Find help in 500+ ports across the Mediterranean, Caribbean, and beyond.',
              },
              {
                icon: Users,
                title: 'Team Support',
                description:
                  'Need multiple helpers for your superyacht? We handle team requests too.',
              },
            ].map((feature, index) => (
              <div key={index} className="card">
                <feature.icon className="h-8 w-8 text-primary-600 mb-4" />
                <h3 className="text-lg font-semibold text-navy-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-navy-500 font-light leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
            Ready to Dock with Confidence?
          </h2>
          <p className="mt-4 text-lg text-navy-500 font-light">
            Join thousands of owners/captains and helpers already using AnchorPals.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn-primary text-base px-8 py-3.5"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              className="btn-secondary text-base px-8 py-3.5"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-100 py-10"
        style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-9 w-9 rounded-lg" />
              <span className="ml-2.5 text-lg font-bold text-navy-700 tracking-tight">
                AnchorPals
              </span>
            </div>
            <div className="flex space-x-8 text-sm text-navy-500">
              <Link to="/terms" className="hover:text-navy-900 transition-colors duration-300">Terms</Link>
              <Link to="/privacy" className="hover:text-navy-900 transition-colors duration-300">Privacy</Link>
              <Link to="/help" className="hover:text-navy-900 transition-colors duration-300">Help</Link>
              <Link to="/contact" className="hover:text-navy-900 transition-colors duration-300">Contact</Link>
            </div>
          </div>
          <div className="mt-6 text-center text-navy-400 text-xs">
            &copy; 2026 AnchorPals. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
